import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <h2 className="text-xl font-semibold text-foreground-950">分类未找到</h2>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `https://caoliu-guide.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://caoliu-guide.com',
    },
  };

  return (
    <Layout>
      <SEOHead
        title={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        keywords={`${category.name},caoliu${category.name},caoliu导航,caoliu指南`}
        canonicalUrl={`https://caoliu-guide.com/category/${category.slug}`}
        schemaJsonLd={[pageSchema]}
      />

      <div className="pt-24 pb-10 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="flex items-start gap-4 mb-2">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-500 flex-shrink-0">
              <i className={`${category.icon} text-2xl text-background-50`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {category.name}
              </h1>
              <p className="text-foreground-600">
                {category.description}
              </p>
              <p className="text-sm text-foreground-500 mt-2">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="py-6 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">切换分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                暂无文章
              </h3>
              <p className="text-foreground-500 mb-6">
                该分类下暂时还没有文章，请稍后再来查看
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="py-10 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-lg font-semibold text-foreground-950">热门分类</h2>
              <p className="text-sm text-foreground-500 mt-1">探索更多 caoliu 相关内容</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.filter(c => c.slug !== slug).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-600 hover:border-primary-200/60 hover:text-primary-600 transition-all"
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}